<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading :module-id="access.module_id" :profile-id="access.profile_id" :reference="access.view_reference" summary-only />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
            <div class="list-view-heading__controls-end">
              <AccountingHelp view="accountingClosing" />
            </div>
          </div>
        </section>
      </b-card>

      <!-- Filtros -->
      <b-card header-tag="header" footer-tag="footer">
        <b-row class="align-items-end">
          <b-col md="2">
            <label class="filter-label">Empresa *</label>
            <b-form-select size="sm" v-model="filters.business_id" :options="arr.businesses" @change="onFilterChange()"></b-form-select>
          </b-col>
          <b-col md="1">
            <label class="filter-label">Tipo *</label>
            <b-form-select size="sm" v-model="filters.declared" :options="arr.select.declared" @change="onFilterChange()"></b-form-select>
          </b-col>
          <b-col md="2">
            <label class="filter-label">Inicio del ejercicio</label>
            <b-form-input type="date" size="sm" v-model="filters.date_start" disabled v-b-tooltip.hover title="Es la fecha de la última apertura"></b-form-input>
          </b-col>
          <b-col md="2">
            <label class="filter-label">Fecha de cierre</label>
            <b-form-input type="date" size="sm" v-model="filters.date_close" @change="onCloseDateChange()"></b-form-input>
          </b-col>
          <b-col md="2">
            <label class="filter-label">Fecha de apertura</label>
            <b-form-input type="date" size="sm" v-model="filters.date_open"></b-form-input>
          </b-col>
          <b-col cols="auto" class="d-flex align-items-end">
            <b-button variant="dark" size="sm" @click="preview()" :disabled="loading || needsOpening">
              <b-icon icon="search" class="mr-1"></b-icon>
              Previsualizar
            </b-button>
          </b-col>
        </b-row>

        <!-- Sin apertura: el ciclo arranca acá -->
        <div v-if="needsOpening" class="closing-info mt-3 text-center">
          <div class="closing-info-title">Todavía no iniciaste la contabilidad</div>
          <p class="small text-muted mb-2">La apertura inicial carga los saldos con los que arrancás y se hace una sola vez por empresa y tipo. Después, cada ejercicio se cierra y se abre desde esta pantalla.</p>
          <b-button variant="dark" size="sm" @click="$refs.openingForm.open()"><AppIcon name="edit_note" class="mr-1" />Cargar apertura inicial</b-button>
        </div>

        <!-- Estado del período -->
        <div v-if="status && !needsOpening" class="closing-status mt-3">
          <div class="closing-status__row">
            <div>
              <small class="closing-status__label">Último cierre</small>
              <div v-if="status.last_closing">
                <b>{{ formatDate(status.last_closing.date) }}</b> · {{ status.last_closing.reference }}
                <span class="text-muted" v-if="status.next_opening">· Apertura siguiente: {{ formatDate(status.next_opening.date) }}</span>
                <span class="text-warning" v-else>· Falta generar la apertura</span>
              </div>
              <div class="text-muted" v-else>Todavía no hay cierres</div>
            </div>
            <span v-if="status.last_closing && access.elements.reopenPeriod"
                  v-b-tooltip.hover :title="status.reopen_block || ''">
              <b-button variant="outline-danger" size="sm" :disabled="!!status.reopen_block" @click="openReopen()">
                <AppIcon name="lock_open" class="mr-1" />Reabrir período
              </b-button>
            </span>
          </div>
          <small class="text-muted d-block mt-1" v-if="status.reopen_block">{{ status.reopen_block }}</small>
          <div class="closing-status__history" v-if="status.reopenings.length">
            <small class="closing-status__label">Reaperturas</small>
            <div v-for="item in status.reopenings" :key="item.id" class="small">
              {{ moment(item.created_at).format('DD/MM/YYYY HH:mm') }} · Cierre {{ formatDate(item.closing_date) }}<span v-if="item.user_name"> · {{ item.user_name }}</span> · {{ item.reason }}
            </div>
          </div>
        </div>

        <!-- Bloqueo mensual -->
        <div v-if="status && !needsOpening" class="closing-status mt-3">
          <div class="closing-status__row">
            <div>
              <small class="closing-status__label">Bloqueo mensual</small>
              <div v-if="status.locked_until">Bloqueado hasta el <b>{{ formatDate(status.locked_until) }}</b></div>
              <div class="text-muted" v-else>Sin meses bloqueados</div>
            </div>
            <div class="closing-lock__actions">
              <b-form-input type="month" size="sm" class="closing-lock__month" aria-label="Mes a bloquear" v-model="lockMonth"></b-form-input>
              <b-button variant="dark" size="sm" :disabled="loading || !lockMonth" @click="confirmLockMonth()"><AppIcon name="lock" class="mr-1" />Bloquear</b-button>
              <span v-if="status.locked_until && access.elements.reopenPeriod" v-b-tooltip.hover :title="status.unlock_block || ''">
                <b-button variant="outline-danger" size="sm" :disabled="!!status.unlock_block" @click="openUnlock()"><AppIcon name="lock_open" class="mr-1" />Desbloquear último mes</b-button>
              </span>
            </div>
          </div>
          <small class="text-muted d-block mt-1" v-if="status.unlock_block && status.locked_until">{{ status.unlock_block }}</small>
          <div class="closing-status__history" v-if="status.month_locks.length">
            <small class="closing-status__label">Últimos cambios</small>
            <div v-for="item in status.month_locks" :key="item.id" class="small">
              {{ moment(item.created_at).format('DD/MM/YYYY HH:mm') }} · {{ item.reason ? 'Desbloqueo' : 'Bloqueo' }}: {{ item.locked_until ? 'hasta ' + formatDate(item.locked_until) : 'sin meses bloqueados' }}<span v-if="item.user_name"> · {{ item.user_name }}</span><span v-if="item.reason"> · {{ item.reason }}</span>
            </div>
          </div>
        </div>

        <!-- Bloque informativo (antes de previsualizar) -->
        <div v-if="!previewData && !needsOpening" class="closing-info mt-4">
          <div class="closing-info-title">¿Qué va a suceder?</div>

          <div class="closing-info-period mt-2 mb-3">
            Se analizarán todos los movimientos contables del período
            <b>{{ formatDate(filters.date_start) }}</b> al <b>{{ formatDate(filters.date_close) }}</b>.
            El inicio de ejercicio marca desde dónde se calculan los saldos; generalmente coincide con la fecha de apertura del ejercicio anterior.
          </div>

          <b-row>
            <b-col md="6">
              <div class="closing-info-step">
                <div class="closing-info-step-num">1</div>
                <div class="closing-info-step-body">
                  <div class="closing-info-step-label">Asiento de Cierre <span class="text-muted font-weight-normal">· {{ formatDate(filters.date_close) }}</span></div>
                  <div class="closing-info-step-text">
                    Salda todas las cuentas de <b>Ingresos</b> y <b>Egresos</b> acumulados en el período contra la cuenta de <b>Resultado del Ejercicio</b>, dejando esas cuentas en cero.
                    El período quedará <b>bloqueado</b>: no se podrán registrar nuevos asientos con fechas anteriores a este cierre.
                  </div>
                </div>
              </div>
            </b-col>
            <b-col md="6">
              <div class="closing-info-step">
                <div class="closing-info-step-num">2</div>
                <div class="closing-info-step-body">
                  <div class="closing-info-step-label">Asiento de Apertura <span class="text-muted font-weight-normal">· {{ formatDate(filters.date_open) }}</span></div>
                  <div class="closing-info-step-text">
                    Reabre las cuentas de <b>Activo</b>, <b>Pasivo</b> y <b>Patrimonio</b> con los saldos al cierre, incorporando el resultado del ejercicio.
                    Esta fecha pasa a ser el nuevo <b>inicio de ejercicio</b> para el próximo cierre.
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>
      </b-card>

      <!-- Previsualización -->
      <div v-if="previewData">
        <b-alert show variant="danger" v-if="previewData.closing_block">{{ previewData.closing_block }}</b-alert>
        <b-alert show variant="danger" v-if="previewData.pending_count">
          Hay {{ previewData.pending_count }} movimiento/s sin asiento en el período. Aprobalos en Asientos Pendientes antes de cerrar.
        </b-alert>

        <!-- Asiento de Cierre -->
        <b-card header-tag="header" footer-tag="footer">
          <b-row class="mb-3">
            <b-col>
              <h5 class="mb-0">
                Asiento de Cierre
                <small class="text-muted ml-2">{{ formatDate(filters.date_close) }}</small>
              </h5>
              <small class="text-muted">Salda todas las cuentas de resultado contra Resultado del Ejercicio</small>
            </b-col>
            <b-col cols="auto">
              <b-badge :variant="previewData.closing.net_result >= 0 ? 'success' : 'danger'" style="font-size:13px; padding:6px 12px;">
                {{ previewData.closing.net_result >= 0 ? 'Ganancia' : 'Pérdida' }}:
                {{ formatCurrency(Math.abs(previewData.closing.net_result)) }}
              </b-badge>
            </b-col>
          </b-row>

          <b-table class="mb-3"
                   responsive="sm"
                   head-variant="dark"
                   :hover="true"
                   :small="true"
                   :items="previewData.closing.lines"
                   :fields="tableFields"
                   :tbody-tr-class="rowClass">
            <template v-slot:cell(code)="data">
              <span :style="data.item.type === 'Haber' ? 'padding-left:2rem;' : ''">
                {{ data.item.accounting_accounts_code }}
              </span>
            </template>
            <template v-slot:cell(name)="data">
              <span :style="data.item.type === 'Haber' ? 'padding-left:2rem;' : ''">
                {{ data.item.accounting_accounts_name }}
              </span>
            </template>
            <template v-slot:cell(debit)="data">
              <b v-if="data.item.type == 'Debe'" class="accounting-closing-amount">{{ formatCurrency(data.item.amount) }}</b>
            </template>
            <template v-slot:cell(credit)="data">
              <b v-if="data.item.type == 'Haber'" class="accounting-closing-amount">{{ formatCurrency(data.item.amount) }}</b>
            </template>
          </b-table>

          <div class="d-flex justify-content-end mb-3">
            <div class="text-center border rounded px-3 py-2 mr-2" style="min-width:150px; background:#fff3cd;">
              <small class="d-block text-muted font-weight-bold text-uppercase" style="font-size:10px;">Debe</small>
              <strong style="font-size:15px;">{{ formatCurrency(previewData.closing.total_debit) }}</strong>
            </div>
            <div class="text-center border rounded px-3 py-2" style="min-width:150px; background:#d1ecf1;">
              <small class="d-block text-muted font-weight-bold text-uppercase" style="font-size:10px;">Haber</small>
              <strong style="font-size:15px;">{{ formatCurrency(previewData.closing.total_credit) }}</strong>
            </div>
          </div>

          <hr>

          <div v-if="!generated.closing">
            <b-row class="align-items-end">
              <b-col md="4">
                <label><b>Referencia</b></label>
                <b-form-input type="text" size="sm" v-model="crud.closing.reference" placeholder="Ej: CIERRE-2024"></b-form-input>
              </b-col>
              <b-col md="6">
                <label><b>Observaciones</b></label>
                <b-form-input type="text" size="sm" v-model="crud.closing.observations"></b-form-input>
              </b-col>
              <b-col md="2" class="d-flex align-items-end">
                <b-button variant="dark" size="sm" block @click="confirmClosing()" :disabled="loading || !!previewData.closing_block || !!previewData.pending_count">
                  Generar cierre
                </b-button>
              </b-col>
            </b-row>
          </div>
          <b-alert v-else variant="success" show class="mb-0">
            <b-icon icon="check-circle" class="mr-1"></b-icon>
            Asiento de cierre generado: <b>{{ generated.closing.reference }}</b>
          </b-alert>
        </b-card>

        <!-- Asiento de Apertura -->
        <b-card header-tag="header" footer-tag="footer">
          <b-row class="mb-3">
            <b-col>
              <h5 class="mb-0">
                Asiento de Apertura
                <small class="text-muted ml-2">{{ formatDate(filters.date_open) }}</small>
              </h5>
              <small class="text-muted">Re-abre las cuentas patrimoniales con los saldos al cierre</small>
            </b-col>
          </b-row>

          <b-table class="mb-3"
                   responsive="sm"
                   head-variant="dark"
                   :hover="true"
                   :small="true"
                   :items="previewData.opening.lines"
                   :fields="tableFields"
                   :tbody-tr-class="rowClass">
            <template v-slot:cell(code)="data">
              <span :style="data.item.type === 'Haber' ? 'padding-left:2rem;' : ''">
                {{ data.item.accounting_accounts_code }}
              </span>
            </template>
            <template v-slot:cell(name)="data">
              <span :style="data.item.type === 'Haber' ? 'padding-left:2rem;' : ''">
                {{ data.item.accounting_accounts_name }}
              </span>
            </template>
            <template v-slot:cell(debit)="data">
              <b v-if="data.item.type == 'Debe'" class="accounting-closing-amount">{{ formatCurrency(data.item.amount) }}</b>
            </template>
            <template v-slot:cell(credit)="data">
              <b v-if="data.item.type == 'Haber'" class="accounting-closing-amount">{{ formatCurrency(data.item.amount) }}</b>
            </template>
          </b-table>

          <div class="d-flex justify-content-end mb-3">
            <div class="text-center border rounded px-3 py-2 mr-2" style="min-width:150px; background:#fff3cd;">
              <small class="d-block text-muted font-weight-bold text-uppercase" style="font-size:10px;">Debe</small>
              <strong style="font-size:15px;">{{ formatCurrency(previewData.opening.total_debit) }}</strong>
            </div>
            <div class="text-center border rounded px-3 py-2" style="min-width:150px; background:#d1ecf1;">
              <small class="d-block text-muted font-weight-bold text-uppercase" style="font-size:10px;">Haber</small>
              <strong style="font-size:15px;">{{ formatCurrency(previewData.opening.total_credit) }}</strong>
            </div>
          </div>

          <hr>

          <div v-if="!generated.opening">
            <b-row class="align-items-end">
              <b-col md="4">
                <label><b>Referencia</b></label>
                <b-form-input type="text" size="sm" v-model="crud.opening.reference" placeholder="Ej: APERTURA-2025"></b-form-input>
              </b-col>
              <b-col md="6">
                <label><b>Observaciones</b></label>
                <b-form-input type="text" size="sm" v-model="crud.opening.observations"></b-form-input>
              </b-col>
              <b-col md="2" class="d-flex align-items-end">
                <b-button variant="dark" size="sm" block @click="generateOpening()" :disabled="loading">
                  Generar apertura
                </b-button>
              </b-col>
            </b-row>
          </div>
          <b-alert v-else variant="success" show class="mb-0">
            <b-icon icon="check-circle" class="mr-1"></b-icon>
            Asiento de apertura generado: <b>{{ generated.opening.reference }}</b>
          </b-alert>
        </b-card>
      </div>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="back" />
            Volver
          </b-button>
        </div>
      </b-card>

      <b-modal v-model="modal.reopen.active" :key="modal.reopen._id" :id="modal.reopen._id">
        <div slot="modal-header" minimize class="app-modal-header app-modal-header--danger">
          <span class="app-modal-header__icon"><AppIcon name="lock_open" /></span>
          <h5 class="app-modal-header__title">Reabrir período</h5>
        </div>

        <div v-if="status && status.last_closing">
          <p class="mb-2">
            Se borra el cierre del <b>{{ formatDate(status.last_closing.date) }}</b> ({{ status.last_closing.reference }})<span v-if="status.next_opening"> y la apertura del <b>{{ formatDate(status.next_opening.date) }}</b></span>.
            El período vuelve a admitir comprobantes y asientos.
          </p>
          <b-form-group label="Motivo *" class="mb-0">
            <b-form-textarea v-model="modal.reopen.reason" rows="3" placeholder="Por qué se reabre el período" />
          </b-form-group>
        </div>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" size="sm" class="mr-1" @click="modal.reopen.active = false"><AppIcon name="close" class="mr-1" />Cancelar</b-button>
          <b-button variant="danger" size="sm" :disabled="loading || !modal.reopen.reason.trim()" @click="reopen()"><AppIcon name="lock_open" class="mr-1" />Reabrir</b-button>
        </div>
      </b-modal>
      <b-modal v-model="modal.unlock.active" :key="modal.unlock._id" :id="modal.unlock._id">
        <div slot="modal-header" minimize class="app-modal-header app-modal-header--danger">
          <span class="app-modal-header__icon"><AppIcon name="lock_open" /></span>
          <h5 class="app-modal-header__title">Desbloquear último mes</h5>
        </div>

        <div v-if="status && status.locked_until">
          <p class="mb-2">
            Se libera el mes de <b>{{ monthName(status.locked_until) }}</b>: vuelve a admitir cambios y sus asientos pierden el número definitivo del Libro Diario.
          </p>
          <b-form-group label="Motivo *" class="mb-0">
            <b-form-textarea v-model="modal.unlock.reason" rows="3" placeholder="Por qué se desbloquea el mes" />
          </b-form-group>
        </div>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" size="sm" class="mr-1" @click="modal.unlock.active = false"><AppIcon name="close" class="mr-1" />Cancelar</b-button>
          <b-button variant="danger" size="sm" :disabled="loading || !modal.unlock.reason.trim()" @click="unlockMonth()"><AppIcon name="lock_open" class="mr-1" />Desbloquear</b-button>
        </div>
      </b-modal>
      <AccountingOpeningForm ref="openingForm" :business-id="filters.business_id" :declared="filters.declared" @saved="loadStatus(true)" />
    </b-col>
  </b-row>
</template>
<script>
  import AccountingHelp from './inc/AccountingHelp'
  import AccountingOpeningForm from './inc/AccountingOpeningForm'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Helper from '@/handler/helper'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import moment from 'moment'

  export default {
    components: {
      AccountingHelp,
      AccountingOpeningForm,
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.CONTABILIDAD,
          profile_id: Profiles.PERSONAL,
          view_reference: 'accountingClosing',
          elements: {}
        },
        filters: {
          date_start: '',
          date_close: '',
          date_open: '',
          business_id: null,
          declared: null,
        },
        arr: {
          businesses: [],
          select: {
            declared: [
              { value: null, text: 'Seleccionar...' },
              { value: true, text: 'D' },
              { value: false, text: 'ND' },
            ],
          }
        },
        tableFields: [
          { key: 'code',   label: 'Código',  class: 'align-middle' },
          { key: 'name',   label: 'Nombre',  class: 'align-middle' },
          { key: 'debit',  label: 'Debe',    class: 'align-middle text-right' },
          { key: 'credit', label: 'Haber',   class: 'align-middle text-right' },
        ],
        previewData: null,
        status: null,
        lockMonth: moment().subtract(1, 'month').format('YYYY-MM'),
        modal: {
          reopen: { active: false, _id: '', reason: '' },
          unlock: { active: false, _id: '', reason: '' },
        },
        loading: false,
        generated: {
          closing: null,
          opening: null,
        },
        crud: {
          closing: { reference: '', observations: 'Asiento de cierre de ejercicio' },
          opening: { reference: '', observations: 'Asiento de apertura de ejercicio' },
        },
      }
    },
    created() {
      Helper.hasAccessView(this.access)
      this.access.elements.reopenPeriod = Helper.hasAccessElement(this.access, 'reopen_period')
    },
    mounted() {
      this._start = true
      this.loadBusinesses()
    },
    // Con keep-alive, al volver: el estado pudo cambiar desde otras pantallas
    activated() {
      if(this._start) {
        this._start = false
        return false
      }

      this.loadStatus()
    },
    computed: {
      needsOpening() {
        return !!this.status && !this.status.period_start
      },
    },
    methods: {
      moment,
      loadBusinesses() {
        serviceAPI.obtenerEmpresas()
          .then(response => {
            const items = response.data || []
            this.arr.businesses = items.map(b => ({ value: b.id, text: b.name || b.reference || String(b.id) }))
            if(this.arr.businesses.length) {
              this.filters.business_id = this.arr.businesses[0].value
            }
            this.initDates()
          })
          .catch(error => {
            this.$awn.alert(Error.showError(error))
          })
      },
      initDates() {
        const year = moment().year() - 1
        this.filters.date_start = year + '-01-01'
        this.filters.date_close = year + '-12-31'
        this.filters.date_open  = (year + 1) + '-01-01'
        this.crud.closing.reference = 'CIERRE-' + year
        this.crud.opening.reference = 'APERTURA-' + (year + 1)
      },
      // Las fechas se sugieren solo al elegir empresa y tipo: después son las que eligió el usuario
      loadStatus(applyDates = false) {
        if(!this.filters.business_id || this.filters.declared === null) {
          this.status = null
          return Promise.resolve()
        }
        return serviceAPI.obtenerEstadoPeriodo({
          business_id: this.filters.business_id,
          declared:    this.filters.declared,
        })
        .then(response => {
          this.status = response.data
          if(applyDates) this.applyPeriodDates()
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      // El ejercicio arranca en la apertura. El cierre se sugiere a fin de ese año.
      applyPeriodDates() {
        const start = this.status.period_start
        this.filters.date_start = start || ''
        if(!start) return

        const year = moment(start).year()
        this.filters.date_close = year + '-12-31'
        this.filters.date_open  = (year + 1) + '-01-01'
        this.crud.closing.reference = 'CIERRE-' + year
        this.crud.opening.reference = 'APERTURA-' + (year + 1)
      },
      confirmClosing() {
        const data = this.previewData.closing
        const result = data.net_result >= 0 ? 'Ganancia' : 'Pérdida'
        const message = 'Se cierra el período del ' + this.formatDate(this.previewData.date_start) + ' al ' + this.formatDate(this.filters.date_close)
                      + '. ' + result + ': ' + this.formatCurrency(Math.abs(data.net_result))
                      + '. El período queda bloqueado: no admite comprobantes ni asientos hasta reabrirlo.'
        const title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: 'lock' } }),
          'Generar cierre'
        ])
        this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'dark',
          okTitle: 'Cerrar período',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          headerClass: 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
        .then(value => {
          if(value) this.generateClosing()
        })
      },
      monthName(date) {
        return moment(date).locale('es').format('MMMM YYYY')
      },
      confirmLockMonth() {
        const message = 'Se bloquea hasta el final de ' + this.monthName(this.lockMonth + '-01')
                      + ': no se podrán cargar, editar ni borrar comprobantes ni asientos de esas fechas, y sus asientos toman el número definitivo del Libro Diario.'
        const title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: 'lock' } }),
          'Bloquear mes'
        ])
        this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'dark',
          okTitle: 'Bloquear',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          headerClass: 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
        .then(value => {
          if(value) this.saveLockMonth()
        })
      },
      saveLockMonth() {
        this.loading = true
        serviceAPI.bloquearMes({
          business_id: this.filters.business_id,
          declared:    this.filters.declared,
          month:       this.lockMonth,
        })
        .then(() => {
          this.$awn.success('Mes bloqueado')
          return this.loadStatus()
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      openUnlock() {
        this.modal.unlock.reason = ''
        this.modal.unlock._id = Math.random().toString(36).slice(2, 8)
        this.modal.unlock.active = true
      },
      unlockMonth() {
        this.loading = true
        serviceAPI.desbloquearMes({
          business_id: this.filters.business_id,
          declared:    this.filters.declared,
          reason:      this.modal.unlock.reason,
        })
        .then(() => {
          this.modal.unlock.active = false
          this.$awn.success('Mes desbloqueado')
          return this.loadStatus()
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      openReopen() {
        this.modal.reopen.reason = ''
        this.modal.reopen._id = Math.random().toString(36).slice(2, 8)
        this.modal.reopen.active = true
      },
      reopen() {
        this.loading = true
        serviceAPI.reabrirPeriodo({
          closing_id: this.status.last_closing.id,
          reason:     this.modal.reopen.reason,
        })
        .then(() => {
          this.modal.reopen.active = false
          this.previewData = null
          this.generated = { closing: null, opening: null }
          this.$awn.success('Período reabierto')
          return this.loadStatus(true)
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      onCloseDateChange() {
        if(this.filters.date_close) {
          const next = moment(this.filters.date_close).add(1, 'day').format('YYYY-MM-DD')
          this.filters.date_open = next
        }
        this.previewData = null
        this.generated = { closing: null, opening: null }
      },
      onFilterChange() {
        this.previewData = null
        this.generated = { closing: null, opening: null }
        this.loadStatus(true)
      },
      rowClass(item) {
        if(!item) return ''
        return item.type === 'Debe' ? 'table-warning' : 'table-info'
      },
      formatDate(date) {
        return date ? moment(date).format('DD/MM/YYYY') : ''
      },
      formatCurrency(value) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value || 0)
      },
      preview() {
        if(!this.filters.business_id) {
          this.$awn.alert('Debe seleccionar la empresa')
          return
        }
        if(this.filters.declared === null || this.filters.declared === undefined) {
          this.$awn.alert('Debe seleccionar el tipo (Declarado / No Declarado)')
          return
        }
        if(!this.filters.date_close) {
          this.$awn.alert('Debe indicar la fecha de cierre')
          return
        }
        this.loading = true
        this.previewData = null
        this.generated = { closing: null, opening: null }

        serviceAPI.previewCierreApertura({
          date_start:  this.filters.date_start,
          date_close:  this.filters.date_close,
          date_open:   this.filters.date_open,
          business_id: this.filters.business_id,
          declared:    this.filters.declared,
        })
        .then(response => {
          this.previewData = response.data
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      generateClosing() {
        if(!this.crud.closing.reference) {
          this.$awn.alert('Debe indicar la referencia del asiento de cierre')
          return
        }
        this.loading = true
        serviceAPI.generarCierre({
          date_start:   this.filters.date_start,
          date_close:   this.filters.date_close,
          reference:    this.crud.closing.reference,
          observations: this.crud.closing.observations,
          business_id:  this.filters.business_id,
          declared:     this.filters.declared,
        })
        .then(response => {
          this.generated.closing = response.data
          this.$awn.success('Asiento de cierre generado con éxito')
          this.loadStatus()
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      generateOpening() {
        if(!this.crud.opening.reference) {
          this.$awn.alert('Debe indicar la referencia del asiento de apertura')
          return
        }
        this.loading = true
        serviceAPI.generarApertura({
          date_start:   this.filters.date_start,
          date_close:   this.filters.date_close,
          date_open:    this.filters.date_open,
          reference:    this.crud.opening.reference,
          observations: this.crud.opening.observations,
          business_id:  this.filters.business_id,
          declared:     this.filters.declared,
        })
        .then(response => {
          this.generated.opening = response.data
          this.$awn.success('Asiento de apertura generado con éxito')
          this.loadStatus()
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
    }
  }
</script>
<style scoped>
  .closing-status {
    padding: 10px 14px;
    background: var(--app-color-surface-subtle);
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
  }
  .closing-status__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  .closing-status__label {
    display: block;
    font-weight: 600;
    color: var(--app-color-text-secondary);
  }
  .closing-lock__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }
  .closing-lock__month {
    width: 160px;
  }
  .closing-status__history {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--app-color-border);
  }
  .accounting-closing-amount {
    font-size: 13px;
  }
  .filter-label {
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 2px;
    color: #495057;
    display: block;
  }
  .closing-info {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 16px 20px;
  }
  .closing-info-title {
    font-size: 13px;
    font-weight: 700;
    color: #495057;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  .closing-info-period {
    font-size: 12px;
    color: #495057;
    border-left: 3px solid #343a40;
    padding-left: 10px;
  }
  .closing-info-step {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-top: 4px;
  }
  .closing-info-step-num {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    background: #343a40;
    color: #fff;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1px;
  }
  .closing-info-step-label {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 4px;
  }
  .closing-info-step-text {
    font-size: 12px;
    color: #495057;
    line-height: 1.5;
  }
</style>
